import React, { useState } from 'react';
import { Loader2, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { recordAccessApi } from '../api/recordAccess.api';

/** Request OTP to patient or guardian, then verify for full record access. */
export default function RecordAccessOtpPanel({ patientId, channels = [], onUnlocked }) {
  const [channel, setChannel] = useState(channels[0]?.key || 'patient');
  const [challengeId, setChallengeId] = useState('');
  const [otp, setOtp] = useState('');
  const [info, setInfo] = useState('');
  const [devOtp, setDevOtp] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const send = async () => {
    setError('');
    setBusy(true);
    try {
      const data = await recordAccessApi.requestOtp(patientId, channel);
      if (data.already_full) {
        onUnlocked?.();
        return;
      }
      setChallengeId(data.challenge_id);
      setInfo(data.message || 'OTP sent.');
      setDevOtp(data.dev_otp || '');
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'string' ? d : d?.message || 'Could not send OTP.');
    }
    setBusy(false);
  };

  const verify = async () => {
    setError('');
    setBusy(true);
    try {
      await recordAccessApi.verifyOtp(patientId, challengeId, otp.trim());
      onUnlocked?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'string' ? d : d?.message || 'Incorrect OTP.');
    }
    setBusy(false);
  };

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/80 p-4 space-y-3" data-testid="record-access-otp">
      <p className="text-sm font-semibold text-[#071B34] flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-[#F15A22]" />
        Unlock full record with OTP
      </p>
      <p className="text-xs text-[#5A6B7C]">
        This patient is not in your current appointment. Send a one-time code to the patient
        or their parent / faculty-staff contact. Full history and prescriptions open after verification.
      </p>
      {channels.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {channels.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => setChannel(c.key)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${
                channel === c.key
                  ? 'bg-[#F15A22] text-white border-[#F15A22]'
                  : 'bg-white text-[#071B34] border-[#D5DEE8]'
              }`}
            >
              {c.label}: {c.name}
              {c.masked_email ? ` · ${c.masked_email}` : ''}
              {c.masked_phone ? ` · ${c.masked_phone}` : ''}
            </button>
          ))}
        </div>
      )}
      {!challengeId ? (
        <Button onClick={send} disabled={busy || !channels.length} className="text-white" style={{ background: '#F15A22' }}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
          Send OTP
        </Button>
      ) : (
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[140px]">
            <Input value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="Enter OTP" className="bg-white" />
          </div>
          <Button onClick={verify} disabled={busy || otp.trim().length < 4} className="text-white" style={{ background: '#F15A22' }}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Verify &amp; unlock
          </Button>
          <Button variant="outline" onClick={send} disabled={busy}>Resend</Button>
        </div>
      )}
      {info ? <p className="text-xs text-emerald-700">{info}</p> : null}
      {devOtp ? <p className="text-xs text-amber-800 font-mono">Demo OTP: {devOtp}</p> : null}
      {error ? <p className="text-xs text-red-600">{error}</p> : null}
      {!channels.length ? (
        <p className="text-xs text-red-600">No patient or guardian contact on file for OTP delivery.</p>
      ) : null}
    </div>
  );
}
